import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(255, 215, 0, 0.5); }
  50% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8); }
  100% { box-shadow: 0 0 5px rgba(255, 215, 0, 0.5); }
`;

const AchievementsContainer = styled.div`
  padding: 30px;
  font-family: 'Ubuntu', sans-serif;
  color: #00ff00;
  background: rgba(0, 0, 0, 0.9);
  height: 100%;
  overflow-y: auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #ffd700;
  margin-bottom: 10px;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #ffd700;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #cccccc;
`;

const AchievementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const AchievementCard = styled.div<{ rank: number }>`
  background: ${props => 
    props.rank <= 3 ? 'rgba(255, 215, 0, 0.2)' : 
    props.rank <= 10 ? 'rgba(192, 192, 192, 0.2)' : 
    'rgba(205, 127, 50, 0.2)'
  };
  border: 2px solid ${props => 
    props.rank <= 3 ? '#ffd700' : 
    props.rank <= 10 ? '#c0c0c0' : 
    '#cd7f32'
  };
  border-radius: 12px;
  padding: 20px;
  position: relative;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    animation: ${props => props.rank <= 3 ? glow : 'none'} 2s infinite;
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => 
    props.rank <= 3 ? '#ffd700' : 
    props.rank <= 10 ? '#c0c0c0' : 
    '#cd7f32'
  };
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
`;

const EventName = styled.h3`
  color: #00ffff;
  margin-bottom: 10px;
`;

const RankInfo = styled.div`
  color: #ffd700;
  font-weight: bold;
  margin-bottom: 10px;
`;

const Participants = styled.div`
  color: #cccccc;
  margin-bottom: 10px;
  font-size: 14px;
`;

const Percentage = styled.div<{ rank: number }>`
  color: ${props => 
    props.rank <= 3 ? '#ffd700' : 
    props.rank <= 10 ? '#00ff00' : 
    '#ffff00'
  };
  font-weight: bold;
  margin-bottom: 10px;
`;

const EventType = styled.span`
  background: rgba(0, 255, 255, 0.2);
  color: #00ffff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid rgba(0, 255, 255, 0.3);
`;

const AchievementsCenter: React.FC = () => {
  const [animatedStats, setAnimatedStats] = useState({
    totalCTFs: 0,
    topRankings: 0,
    winRate: 0
  });

  const achievements = [
    {
      event: 'H7CTF (International)',
      rank: 2,
      totalParticipants: '1000+',
      percentage: 0.2,
      year: '2024',
      type: 'International CTF'
    },
    {
      event: 'Lakshya CTF',
      rank: 5,
      totalParticipants: '200+',
      percentage: 2.5,
      year: '2024',
      type: 'National CTF'
    },
    {
      event: 'DY Patil\'s Offline CTF',
      rank: 2,
      totalParticipants: '100+',
      percentage: 2.0,
      year: '2024',
      type: 'Offline CTF'
    },
    {
      event: 'Vishwa CTF Mini',
      rank: 11,
      totalParticipants: '900+',
      percentage: 1.2,
      year: '2025',
      type: 'Online CTF'
    },
    {
      event: 'Technex CTF',
      rank: 13,
      totalParticipants: '250+',
      percentage: 5.2,
      year: '2025',
      type: 'Technical CTF'
    },
    {
      event: 'EnigmaCTF',
      rank: 15,
      totalParticipants: '250+',
      percentage: 6.0,
      year: '2024',
      type: 'Cryptography CTF'
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedStats({
        totalCTFs: achievements.length,
        topRankings: achievements.filter(a => a.rank <= 10).length,
        winRate: Math.round((achievements.filter(a => a.rank <= 15).length / achievements.length) * 100)
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <AchievementsContainer>
      <Header>
        <Title>🏆 ACHIEVEMENTS CENTER</Title>
        <p>Cybersecurity Competition Rankings & Awards</p>
      </Header>

      <StatsBar>
        <StatItem>
          <StatNumber>{animatedStats.totalCTFs}</StatNumber>
          <StatLabel>CTF Competitions</StatLabel>
        </StatItem>
        <StatItem>
          <StatNumber>{animatedStats.topRankings}</StatNumber>
          <StatLabel>Top 15 Rankings</StatLabel>
        </StatItem>
    
        <StatItem>
          <StatNumber>Top 1%</StatNumber>
          <StatLabel>TryHackMe Ranking</StatLabel>
        </StatItem>
      </StatsBar>

      <AchievementGrid>
        {achievements.map((achievement, index) => (
          <AchievementCard key={index} rank={achievement.rank}>
            <RankBadge rank={achievement.rank}>#{achievement.rank}</RankBadge>
            <EventName>{achievement.event}</EventName>
            <RankInfo>Rank {achievement.rank} out of {achievement.totalParticipants} teams</RankInfo>
            <Participants>📊 Total Participants: {achievement.totalParticipants}</Participants>
            <Percentage rank={achievement.rank}>
              🎯 Top {achievement.percentage}% Performance
            </Percentage>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <EventType>{achievement.type}</EventType>
              <span style={{ color: '#cccccc', fontSize: '12px' }}>{achievement.year}</span>
            </div>
          </AchievementCard>
        ))}
      </AchievementGrid>
    </AchievementsContainer>
  );
};

export default AchievementsCenter;
